/**
 * Search Limit Cohorts — product-managed daily load-search caps for the mobile app.
 *
 * A cohort overrides the daily plan limit for a pasted list of emails, for a fixed window.
 * It applies to free, paid, and unlimited users — see docs/ADR/ADR-021-Search-Limit-Cohorts.md.
 */
import * as React from 'react'
import axios from 'axios'
import { SEARCH_COHORT_MAX_EMAILS, SEARCH_COHORT_MAX_LIMIT, SEARCH_GLOBAL_MIN_LIMIT, parseCohortEmails } from '@numeo/types'
import { FREE_DAILY_SEARCH_LIMIT } from '@numeo/types-core'
import { CircularProgress } from '@mui/joy'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import { Add, Delete, Edit, Groups, Public, Search, Warning } from '@mui/icons-material'
import { motion } from 'framer-motion'

const ease = [0.23, 1, 0.32, 1]

interface CohortSummary {
    _id: string
    name: string
    dailyLimit: number
    startsAt: string
    endsAt: string
    emailCount: number
    enabled: boolean
    createdBy: string
    createdAt: string
}

interface Overlap {
    name: string
    emails: string[]
}

/** The global free-tier override. Both dates are optional: no start = live now, no end = open-ended. */
interface GlobalOverride {
    dailyLimit: number
    startsAt?: string
    endsAt?: string
    enabled: boolean
    updatedBy: string
}

/** The live quota loads-ingest resolves today — plan limit + cohort override + referral bonus. */
interface LiveQuota {
    usage_count: number
    limit_count: number
    unlimited: boolean
    remaining: number | null
    reset_at: string
}

interface ResolveResult {
    email: string
    userId: string | null
    registered: boolean
    cohort: { dailyLimit: number; endsAt: string } | null
    live: LiveQuota | null
}

type CohortStatus = 'Active' | 'Scheduled' | 'Expired' | 'Disabled'

function statusOf(cohort: CohortSummary): CohortStatus {
    if (!cohort.enabled) return 'Disabled'
    const now = Date.now()
    if (new Date(cohort.startsAt).getTime() > now) return 'Scheduled'
    if (new Date(cohort.endsAt).getTime() <= now) return 'Expired'
    return 'Active'
}

const STATUS_STYLES: Record<CohortStatus, string> = {
    Active: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200',
    Scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
    Expired: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300',
    Disabled: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200',
}

/** `datetime-local` needs `YYYY-MM-DDTHH:mm` in LOCAL time; toISOString would shift by the offset. */
function toLocalInput(iso: string): string {
    const date = new Date(iso)
    const offset = date.getTimezoneOffset() * 60_000
    return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

function formatWindow(cohort: CohortSummary): string {
    const fmt = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    return `${fmt(cohort.startsAt)} → ${fmt(cohort.endsAt)}`
}

const DEFAULT_WINDOW_DAYS = 14

/**
 * A new cohort starts now and runs for two weeks. Blank dates would disable the Create button with
 * no visible reason — the operator's most common path is "start it today", so make that the default.
 */
function makeEmptyForm() {
    const now = new Date()
    const end = new Date(now.getTime() + DEFAULT_WINDOW_DAYS * 86_400_000)
    return { name: '', dailyLimit: '15', startsAt: toLocalInput(now.toISOString()), endsAt: toLocalInput(end.toISOString()), emails: '', enabled: true }
}

/** Why the Create/Save button is disabled, so a dead button always explains itself. */
function saveBlockers(
    form: ReturnType<typeof makeEmptyForm>,
    parsedEmails: string[],
    invalidLimit: boolean,
    invalidWindow: boolean,
    tooManyEmails: boolean
): string[] {
    const blockers: string[] = []
    if (!form.name.trim()) blockers.push('a name')
    if (invalidLimit) blockers.push(`a daily limit between 0 and ${SEARCH_COHORT_MAX_LIMIT}`)
    if (!form.startsAt || !form.endsAt) blockers.push('a start and end date')
    else if (invalidWindow) blockers.push('an end date after the start date')
    if (parsedEmails.length === 0) blockers.push('at least one email')
    if (tooManyEmails) blockers.push(`no more than ${SEARCH_COHORT_MAX_EMAILS} emails`)
    return blockers
}

/**
 * Why the configured cohort limit and the live enforced limit can differ. Each branch is a real,
 * expected state — not an error — and naming it is the entire value of this lookup.
 */
function explainLookup(result: ResolveResult, activeGlobalLimit: number | null): { text: string; tone: 'ok' | 'warn' | 'info' } {
    if (!result.registered) {
        return result.cohort
            ? { text: 'No account yet. The cohort limit will apply the moment this person signs up.', tone: 'info' }
            : { text: 'No account, and no active cohort covers this email.', tone: 'info' }
    }
    if (!result.live) {
        return { text: 'Could not reach loads-ingest to read the live limit. The cohort config above is still authoritative.', tone: 'warn' }
    }
    if (!result.cohort) {
        if (result.live.unlimited) {
            return { text: 'No active cohort covers this email — the live plan is unlimited.', tone: 'info' }
        }
        if (activeGlobalLimit !== null && result.live.limit_count === activeGlobalLimit) {
            return { text: `No cohort covers this email, so the active global free-tier limit of ${activeGlobalLimit}/day applies.`, tone: 'ok' }
        }
        return { text: 'No active cohort covers this email — the live limit is their plan limit.', tone: 'info' }
    }
    if (result.live.unlimited) {
        return { text: 'The configured cohort is not reflected in the live quota yet. Refresh after cache invalidation or check loads-ingest.', tone: 'warn' }
    }
    if (result.live.limit_count === result.cohort.dailyLimit) {
        const note = activeGlobalLimit !== null ? ' The cohort overrides the active global limit.' : ''
        return { text: `The cohort is in effect. This is the limit the mobile app enforces.${note}`, tone: 'ok' }
    }
    if (result.live.limit_count > result.cohort.dailyLimit) {
        return {
            text: `Live limit (${result.live.limit_count}) is above the cohort's ${result.cohort.dailyLimit}. This can be a referral bonus or a propagation delay.`,
            tone: 'warn',
        }
    }
    return {
        text: `Live limit (${result.live.limit_count}) is below this cohort's ${result.cohort.dailyLimit} — another active cohort with a lower limit wins.`,
        tone: 'info',
    }
}

/** Pull the server-provided message off an axios-style error, falling back to a generic one. */
function errMessage(err: unknown, fallback: string): string {
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    return typeof msg === 'string' && msg ? msg : fallback
}

const TONE_STYLES: Record<'ok' | 'warn' | 'info', string> = {
    ok: 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200',
    warn: 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200',
    info: 'bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
}

export default function SearchCohortsPage() {
    const [cohorts, setCohorts] = React.useState<CohortSummary[]>([])
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState<string | null>(null)
    const [success, setSuccess] = React.useState<string | null>(null)
    const [overlaps, setOverlaps] = React.useState<Overlap[]>([])

    const [globalActive, setGlobalActive] = React.useState(false)
    // The PERSISTED active limit, kept separate from the editable form field so the lookup
    // explanation reflects what's enforced, not what the operator is mid-typing (unsaved).
    const [activeGlobalDailyLimit, setActiveGlobalDailyLimit] = React.useState<number | null>(null)
    const [globalForm, setGlobalForm] = React.useState({ dailyLimit: '', startsAt: '', endsAt: '', enabled: true })
    const [globalExists, setGlobalExists] = React.useState(false)
    const [savingGlobal, setSavingGlobal] = React.useState(false)

    const [lookupEmail, setLookupEmail] = React.useState('')
    const [lookup, setLookup] = React.useState<ResolveResult | null>(null)
    const [lookupError, setLookupError] = React.useState<string | null>(null)
    const [lookingUp, setLookingUp] = React.useState(false)

    const [modalOpen, setModalOpen] = React.useState(false)
    const [editingId, setEditingId] = React.useState<string | null>(null)
    const [form, setForm] = React.useState(makeEmptyForm)
    const [saving, setSaving] = React.useState(false)
    const [deletingId, setDeletingId] = React.useState<string | null>(null)

    const fetchCohorts = React.useCallback(async () => {
        setLoading(true)
        try {
            const res = await axios.get('/gluon/search-cohorts')
            setCohorts(res.data.data?.items ?? [])
        } catch (err) {
            setError(errMessage(err, 'Failed to load cohorts'))
        } finally {
            setLoading(false)
        }
    }, [])

    const fetchGlobal = React.useCallback(async () => {
        try {
            const res = await axios.get('/gluon/search-cohorts/global')
            const override: GlobalOverride | null = res.data.data?.override ?? null
            const active = Boolean(res.data.data?.active)
            setGlobalActive(active)
            setActiveGlobalDailyLimit(active && override ? override.dailyLimit : null)
            setGlobalExists(Boolean(override))
            setGlobalForm({
                dailyLimit: override ? String(override.dailyLimit) : '',
                startsAt: override?.startsAt ? toLocalInput(override.startsAt) : '',
                endsAt: override?.endsAt ? toLocalInput(override.endsAt) : '',
                enabled: override?.enabled ?? true,
            })
        } catch (err) {
            setError(errMessage(err, 'Failed to load the global limit'))
        }
    }, [])

    React.useEffect(() => {
        fetchCohorts()
        fetchGlobal()
    }, [fetchCohorts, fetchGlobal])

    // Parse as the operator types so the count reflects what will actually be saved
    // (deduped + lowercased), not how many lines they pasted.
    const parsedEmails = React.useMemo(() => parseCohortEmails(form.emails), [form.emails])

    const handleSaveGlobal = async () => {
        setSavingGlobal(true)
        setError(null)
        try {
            await axios.put('/gluon/search-cohorts/global', {
                dailyLimit: Number(globalForm.dailyLimit),
                // Blank means "no bound" — the API treats '' as absent.
                startsAt: globalForm.startsAt ? new Date(globalForm.startsAt).toISOString() : '',
                endsAt: globalForm.endsAt ? new Date(globalForm.endsAt).toISOString() : '',
                enabled: globalForm.enabled,
            })
            setSuccess('Global limit saved')
            fetchGlobal()
        } catch (err) {
            setError(errMessage(err, 'Failed to save the global limit'))
        } finally {
            setSavingGlobal(false)
        }
    }

    const handleClearGlobal = async () => {
        if (!window.confirm(`Remove the global limit? Every free user reverts to the default of ${FREE_DAILY_SEARCH_LIMIT}/day.`)) return
        setSavingGlobal(true)
        try {
            await axios.delete('/gluon/search-cohorts/global')
            setSuccess('Global limit removed')
            fetchGlobal()
        } catch (err) {
            setError(errMessage(err, 'Failed to remove the global limit'))
        } finally {
            setSavingGlobal(false)
        }
    }

    const handleLookup = async () => {
        const email = lookupEmail.trim()
        if (!email) return
        setLookingUp(true)
        setLookupError(null)
        setLookup(null)
        try {
            const res = await axios.get('/gluon/search-cohorts/resolve', { params: { email } })
            setLookup(res.data.data)
        } catch (err) {
            setLookupError(errMessage(err, 'Lookup failed'))
        } finally {
            setLookingUp(false)
        }
    }

    const openCreate = () => {
        setEditingId(null)
        setForm(makeEmptyForm())
        setOverlaps([])
        setError(null)
        setModalOpen(true)
    }

    const openEdit = async (id: string) => {
        setError(null)
        setOverlaps([])
        try {
            // The list endpoint returns only a count; fetch the full doc for its email list.
            const res = await axios.get(`/gluon/search-cohorts/${id}`)
            const doc = res.data.data
            setForm({
                name: doc.name,
                dailyLimit: String(doc.dailyLimit),
                startsAt: toLocalInput(doc.startsAt),
                endsAt: toLocalInput(doc.endsAt),
                emails: (doc.emails ?? []).join('\n'),
                enabled: doc.enabled,
            })
            setEditingId(id)
            setModalOpen(true)
        } catch (err) {
            setError(errMessage(err, 'Failed to load cohort'))
        }
    }

    const handleSave = async () => {
        setSaving(true)
        setError(null)
        setOverlaps([])
        try {
            const body = {
                name: form.name,
                dailyLimit: Number(form.dailyLimit),
                // datetime-local yields a local wall-clock string; `new Date()` reads it in the
                // operator's zone, and toISOString normalizes to UTC for the API.
                startsAt: new Date(form.startsAt).toISOString(),
                endsAt: new Date(form.endsAt).toISOString(),
                emails: form.emails,
                enabled: form.enabled,
            }
            const res = editingId ? await axios.put(`/gluon/search-cohorts/${editingId}`, body) : await axios.post('/gluon/search-cohorts', body)

            const returned: Overlap[] = res.data.data?.overlaps ?? []
            setOverlaps(returned)
            setSuccess(editingId ? 'Cohort updated' : 'Cohort created')
            setModalOpen(false)
            fetchCohorts()
        } catch (err) {
            setError(errMessage(err, 'Failed to save cohort'))
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async (cohort: CohortSummary) => {
        if (!window.confirm(`Delete "${cohort.name}"? Its ${cohort.emailCount} members revert to their plan limit.`)) return
        setDeletingId(cohort._id)
        try {
            await axios.delete(`/gluon/search-cohorts/${cohort._id}`)
            setSuccess('Cohort deleted')
            fetchCohorts()
        } catch (err) {
            setError(errMessage(err, 'Failed to delete cohort'))
        } finally {
            setDeletingId(null)
        }
    }

    const invalidWindow = Boolean(form.startsAt && form.endsAt && new Date(form.endsAt) <= new Date(form.startsAt))
    const limitNum = Number(form.dailyLimit)
    const invalidLimit = !Number.isInteger(limitNum) || limitNum < 0 || limitNum > SEARCH_COHORT_MAX_LIMIT
    const tooManyEmails = parsedEmails.length > SEARCH_COHORT_MAX_EMAILS
    const globalLimitNum = Number(globalForm.dailyLimit)
    const globalInvalidWindow = Boolean(globalForm.startsAt && globalForm.endsAt && new Date(globalForm.endsAt) <= new Date(globalForm.startsAt))
    // A global 0 would block search for every free user at once, so the minimum is 1 (the API rejects 0 too).
    const canSaveGlobal =
        globalForm.dailyLimit !== '' &&
        Number.isInteger(globalLimitNum) &&
        globalLimitNum >= SEARCH_GLOBAL_MIN_LIMIT &&
        globalLimitNum <= SEARCH_COHORT_MAX_LIMIT &&
        !globalInvalidWindow

    const blockers = saveBlockers(form, parsedEmails, invalidLimit, invalidWindow, tooManyEmails)
    const canSave = blockers.length === 0

    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} className="p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-semibold flex items-center gap-2 text-slate-900 dark:text-slate-100">
                        <Groups /> Search Limit Cohorts
                    </h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Override the daily search limit for a list of emails, for a fixed window. Cohorts apply to free and paid plans.
                    </p>
                </div>
                <button onClick={openCreate} className="flex items-center gap-1 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition">
                    <Add fontSize="small" /> New Cohort
                </button>
            </div>

            {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
            {success && <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm">{success}</div>}

            {overlaps.length > 0 && (
                <div className="mb-4 p-3 rounded-lg bg-yellow-50 text-yellow-800 text-sm">
                    <div className="flex items-center gap-1 font-medium">
                        <Warning fontSize="small" /> Some emails are already in another active cohort
                    </div>
                    <p className="mt-1 text-yellow-700">The lowest daily limit wins for those users. Saved anyway.</p>
                    <ul className="mt-2 list-disc list-inside">
                        {overlaps.map((overlap) => (
                            <li key={overlap.name}>
                                <span className="font-medium">{overlap.name}</span>: {overlap.emails.slice(0, 5).join(', ')}
                                {overlap.emails.length > 5 && ` (+${overlap.emails.length - 5} more)`}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="mb-6 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                        <Public fontSize="small" /> Global free-tier limit
                    </div>
                    {globalExists && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${globalActive ? STATUS_STYLES.Active : STATUS_STYLES.Disabled}`}>
                            {globalActive ? 'Active' : 'Inactive'}
                        </span>
                    )}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    Replaces the {FREE_DAILY_SEARCH_LIMIT}/day default for <strong>every</strong> free user. Both dates are optional — leave them blank to start
                    now and run until you turn it off. An email cohort is more specific and overrides this, so a cohort can still exempt a group.
                </p>

                <div className="grid grid-cols-4 gap-3 items-end">
                    <label className="text-sm">
                        <span className="text-slate-600 dark:text-slate-400">Daily limit</span>
                        <input
                            type="number"
                            min={SEARCH_GLOBAL_MIN_LIMIT}
                            max={SEARCH_COHORT_MAX_LIMIT}
                            value={globalForm.dailyLimit}
                            onChange={(e) => setGlobalForm({ ...globalForm, dailyLimit: e.target.value })}
                            placeholder={String(FREE_DAILY_SEARCH_LIMIT)}
                            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                        />
                    </label>
                    <label className="text-sm">
                        <span className="text-slate-600 dark:text-slate-400">Starts at (optional)</span>
                        <input
                            type="datetime-local"
                            value={globalForm.startsAt}
                            onChange={(e) => setGlobalForm({ ...globalForm, startsAt: e.target.value })}
                            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                        />
                    </label>
                    <label className="text-sm">
                        <span className="text-slate-600 dark:text-slate-400">Ends at (optional)</span>
                        <input
                            type="datetime-local"
                            value={globalForm.endsAt}
                            onChange={(e) => setGlobalForm({ ...globalForm, endsAt: e.target.value })}
                            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                        />
                    </label>
                    <div className="flex items-center gap-2 pb-2">
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={globalForm.enabled} onChange={(e) => setGlobalForm({ ...globalForm, enabled: e.target.checked })} />
                            <span className="text-slate-600 dark:text-slate-400">Enabled</span>
                        </label>
                    </div>
                </div>

                {globalInvalidWindow && <p className="mt-2 text-xs text-red-600">Ends at must be after starts at.</p>}

                <div className="flex items-center gap-2 mt-3">
                    <button
                        onClick={handleSaveGlobal}
                        disabled={!canSaveGlobal || savingGlobal}
                        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 text-sm"
                    >
                        {savingGlobal ? 'Saving…' : globalExists ? 'Update global limit' : 'Set global limit'}
                    </button>
                    {globalExists && (
                        <button
                            onClick={handleClearGlobal}
                            disabled={savingGlobal}
                            className="px-4 py-2 rounded-lg border border-slate-300 text-sm disabled:opacity-50"
                        >
                            Remove
                        </button>
                    )}
                    {!canSaveGlobal && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                            Needs a daily limit between {SEARCH_GLOBAL_MIN_LIMIT} and {SEARCH_COHORT_MAX_LIMIT}.
                        </span>
                    )}
                </div>
            </div>

            <div className="mb-6 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                    <Search fontSize="small" /> Check a user's effective limit
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    Shows what the mobile app will actually enforce for this email right now, and why it may differ from the cohort you configured.
                </p>
                <div className="flex gap-2">
                    <input
                        value={lookupEmail}
                        onChange={(e) => setLookupEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                        placeholder="driver@example.com"
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                    <button
                        onClick={handleLookup}
                        disabled={!lookupEmail.trim() || lookingUp}
                        className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50 text-sm"
                    >
                        {lookingUp ? 'Checking…' : 'Check'}
                    </button>
                </div>

                {lookupError && <div className="mt-3 p-2 rounded-lg bg-red-50 text-red-700 text-sm">{lookupError}</div>}

                {lookup && (
                    <div className="mt-3">
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                                <div className="text-xs text-slate-500 dark:text-slate-400">Cohort limit (configured)</div>
                                <div className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                                    {lookup.cohort ? `${lookup.cohort.dailyLimit}/day` : '—'}
                                </div>
                            </div>
                            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950">
                                <div className="text-xs text-blue-600 dark:text-blue-300">Live limit (enforced)</div>
                                <div className="text-xl font-semibold text-blue-800 dark:text-blue-200">
                                    {lookup.live ? (lookup.live.unlimited ? 'Unlimited' : `${lookup.live.limit_count}/day`) : '—'}
                                </div>
                            </div>
                            <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                                <div className="text-xs text-slate-500 dark:text-slate-400">Used today</div>
                                <div className="text-xl font-semibold text-slate-900 dark:text-slate-100">{lookup.live ? lookup.live.usage_count : '—'}</div>
                            </div>
                        </div>
                        {(() => {
                            const { text, tone } = explainLookup(lookup, activeGlobalDailyLimit)
                            return <div className={`mt-3 p-3 rounded-lg text-sm ${TONE_STYLES[tone]}`}>{text}</div>
                        })()}
                        {lookup.live && !lookup.live.unlimited && (
                            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                                Resets {new Date(lookup.live.reset_at).toLocaleString()} · {lookup.live.remaining} search
                                {lookup.live.remaining === 1 ? '' : 'es'} remaining
                            </p>
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <CircularProgress />
                </div>
            ) : cohorts.length === 0 ? (
                <div className="text-center py-16 text-slate-500 dark:text-slate-400">No cohorts yet. Create one to manage a group’s daily limit.</div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 dark:bg-slate-800 text-left text-slate-600 dark:text-slate-300">
                            <tr>
                                <th className="px-4 py-3 font-medium">Name</th>
                                <th className="px-4 py-3 font-medium">Daily limit</th>
                                <th className="px-4 py-3 font-medium">Window</th>
                                <th className="px-4 py-3 font-medium">Members</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                                <th className="px-4 py-3 font-medium">Created by</th>
                                <th className="px-4 py-3 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cohorts.map((cohort) => {
                                const status = statusOf(cohort)
                                return (
                                    <tr key={cohort._id} className="border-t border-slate-200 dark:border-slate-700">
                                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{cohort.name}</td>
                                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{cohort.dailyLimit}/day</td>
                                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{formatWindow(cohort)}</td>
                                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{cohort.emailCount}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[status]}`}>{status}</span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{cohort.createdBy}</td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => openEdit(cohort._id)}
                                                className="p-1 text-slate-500 dark:text-slate-400 hover:text-blue-600"
                                                aria-label="Edit cohort"
                                            >
                                                <Edit fontSize="small" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(cohort)}
                                                disabled={deletingId === cohort._id}
                                                className="p-1 text-slate-500 dark:text-slate-400 hover:text-red-600 disabled:opacity-50"
                                                aria-label="Delete cohort"
                                            >
                                                <Delete fontSize="small" />
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <ModalDialog sx={{ maxWidth: 640, width: '100%' }}>
                    <ModalClose />
                    <h2 className="text-lg font-semibold mb-4">{editingId ? 'Edit cohort' : 'New cohort'}</h2>

                    <div className="grid grid-cols-2 gap-4">
                        <label className="col-span-2 text-sm">
                            <span className="text-slate-600">Name</span>
                            <input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Grandfathered Go users"
                                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                            />
                        </label>

                        <label className="text-sm">
                            <span className="text-slate-600">Daily search limit</span>
                            <input
                                type="number"
                                min={0}
                                max={SEARCH_COHORT_MAX_LIMIT}
                                value={form.dailyLimit}
                                onChange={(e) => setForm({ ...form, dailyLimit: e.target.value })}
                                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                            />
                            <span className="text-xs text-slate-400 dark:text-slate-500">
                                Free default is {FREE_DAILY_SEARCH_LIMIT}/day. The cohort overrides paid and unlimited plans too; 0 blocks search entirely.
                            </span>
                        </label>

                        <label className="text-sm flex items-end gap-2 pb-6">
                            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                            <span className="text-slate-600">Enabled</span>
                        </label>

                        <label className="text-sm">
                            <span className="text-slate-600">Starts at</span>
                            <input
                                type="datetime-local"
                                value={form.startsAt}
                                onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
                                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                            />
                        </label>

                        <label className="text-sm">
                            <span className="text-slate-600">Ends at</span>
                            <input
                                type="datetime-local"
                                value={form.endsAt}
                                onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
                                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                            />
                            {invalidWindow && <span className="text-xs text-red-600">Must be after the start.</span>}
                        </label>

                        <label className="col-span-2 text-sm">
                            <span className="text-slate-600">Emails</span>
                            <textarea
                                rows={8}
                                value={form.emails}
                                onChange={(e) => setForm({ ...form, emails: e.target.value })}
                                placeholder={'Paste emails — separated by commas, semicolons, spaces, or newlines.\n\na@example.com\nb@example.com'}
                                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg font-mono text-xs"
                            />
                            <span className={`text-xs ${tooManyEmails ? 'text-red-600 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
                                {parsedEmails.length} unique email{parsedEmails.length === 1 ? '' : 's'}
                                {tooManyEmails && ` — exceeds the ${SEARCH_COHORT_MAX_EMAILS} limit`}
                            </span>
                        </label>
                    </div>

                    <div className="mt-4 p-3 rounded-lg bg-yellow-50 text-xs text-yellow-800">
                        This limit overrides every member’s plan, including paid and unlimited plans. Members in more than one active cohort get the lowest
                        limit. Removing or expiring the cohort restores each member’s plan limit.
                    </div>

                    {error && <div className="mt-3 p-2 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

                    <div className="flex justify-end items-center gap-3 mt-4">
                        {!canSave && <span className="text-xs text-slate-500 dark:text-slate-400 mr-auto">Needs {blockers.join(', ')}.</span>}
                        <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg border border-slate-300">
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!canSave || saving}
                            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create cohort'}
                        </button>
                    </div>
                </ModalDialog>
            </Modal>
        </motion.div>
    )
}
